import React, { useState, useEffect } from 'react';
import { ArrowLeft, ClipboardList } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Patient } from '../types/patient';
import CompleteClinicalWorkflow, { WorkflowResult } from './CompleteClinicalWorkflow';
import FileUpload, { FileUploadCompletePayload } from './FileUpload';
import { FormDataMapper } from '../services/formDataMapper';
import type { ExtractedFileData } from '../types/enhancedSystem';
import { birthDateFromAge, isValidPatientId, parseFullName } from '../utils/patientNameParser';
import { storageService, WorkflowDraft } from '../services/unifiedStorageService';

interface ClinicalWorkflowLocationState {
  restoreDraftId?: string;
}

const ClinicalWorkflow: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<'upload' | 'workflow'>('upload');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [autoFillData, setAutoFillData] = useState<any>(null);
  const [sourceFileName, setSourceFileName] = useState<string>('');
  const [restoredDraft, setRestoredDraft] = useState<WorkflowDraft | null>(null);

  useEffect(() => {
    const state = location.state as ClinicalWorkflowLocationState | FileUploadCompletePayload | null;
    if (!state) return;

    if ('restoreDraftId' in state && state.restoreDraftId) {
      const draft = storageService
        .getAllWorkflowDrafts()
        .find((item) => item.id === state.restoreDraftId);
      if (draft) {
        setSelectedPatient(draft.patient);
        setAutoFillData(draft.initialFormData ?? null);
        setSourceFileName(draft.sourceFileName ?? '');
        setRestoredDraft(draft);
        setPhase('workflow');
      }
      return;
    }

    if ('autoFillMode' in state || 'extractedData' in state || 'fallbackMode' in state) {
      applyFileData(state as FileUploadCompletePayload);
    }
  }, [location.state]);

  const applyFileData = (payload: FileUploadCompletePayload) => {
    const { autoFillMode, extractedData, sourceFile, fallbackMode } = payload;

    if (!autoFillMode || !extractedData) {
      if (fallbackMode) {
        startWithBlankPatient();
      }
      return;
    }

    setSourceFileName(sourceFile || 'archivo_procesado');

    const mappingResult = FormDataMapper.mapAllFormData(extractedData as ExtractedFileData);
    const mappedPatient = mappingResult.patient;
    const rawPatient = extractedData.patient;
    const parsedName = parseFullName(rawPatient?.name || '');

    const autoPatient: Patient = {
      id: mappedPatient.id || (isValidPatientId(rawPatient?.id || '') ? rawPatient!.id : `FMG-${Date.now().toString().slice(-6)}`),
      patientId: mappedPatient.patientId,
      firstName: mappedPatient.firstName || parsedName.firstName,
      lastName: mappedPatient.lastName || parsedName.lastName,
      dateOfBirth: mappedPatient.dateOfBirth || (rawPatient?.age ? birthDateFromAge(rawPatient.age) : ''),
      age: mappedPatient.age ?? rawPatient?.age ?? undefined,
      sex: mappedPatient.sex || rawPatient?.sex || 'other',
      medicalHistory: {
        previousDiseases: [],
        surgeries: [],
        currentMedications: [],
        allergies: [],
      },
      contact: { phone: '', email: '', address: '' },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setSelectedPatient(autoPatient);
    setAutoFillData({
      ...mappingResult,
      clinicalNotes: extractedData.clinicalNotes,
      genieClinicalAnswers: extractedData.genieClinicalAnswers,
      metadata: {
        confidence: extractedData.quality?.confidence || mappingResult.overall.overallConfidence,
        source: 'enhanced_file_processor',
        processingTime: extractedData.quality?.processingTime || 0,
        warnings: [
          ...(extractedData.warnings || []),
          ...mappingResult.overall.warnings,
        ],
      },
    });
    setPhase('workflow');
  };

  const startWithBlankPatient = () => {
    setSelectedPatient({
      id: `FMG-${Date.now().toString().slice(-6)}`,
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      sex: 'other',
      medicalHistory: {
        previousDiseases: [],
        surgeries: [],
        currentMedications: [],
        allergies: [],
      },
      contact: { phone: '', email: '', address: '' },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setAutoFillData(null);
    setPhase('workflow');
  };

  const handleFileUploadComplete = (payload: FileUploadCompletePayload) => {
    applyFileData(payload);
  };

  const handleWorkflowComplete = (result: WorkflowResult) => {
    navigate('/evaluation-complete', { state: { result } });
  };

  const handleBackToUpload = () => {
    setPhase('upload');
    setSelectedPatient(null);
    setAutoFillData(null);
    setSourceFileName('');
    setRestoredDraft(null);
  };

  if (phase === 'workflow' && selectedPatient) {
    return (
      <CompleteClinicalWorkflow
        patient={selectedPatient}
        initialFormData={autoFillData}
        sourceFileName={sourceFileName}
        restoredDraft={restoredDraft}
        onComplete={handleWorkflowComplete}
        onBack={handleBackToUpload}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Volver al inicio
        </button>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 mb-2">
            Evaluación Electrodiagnóstica
          </h1>
          <p className="text-gray-400">
            Sube un archivo con datos electrodiagnósticos o inicia una evaluación manual
          </p>
        </div>

        <FileUpload embedded onComplete={handleFileUploadComplete} />

        <div className="mt-8 flex items-center gap-4">
          <div className="flex-1 h-px bg-gray-700" />
          <span className="text-gray-500 text-sm">o</span>
          <div className="flex-1 h-px bg-gray-700" />
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={startWithBlankPatient}
            className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-full hover:from-blue-500 hover:to-purple-500 transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900"
          >
            <ClipboardList className="w-5 h-5 mr-2" />
            Iniciar evaluación manual
          </button>
          <p className="text-gray-500 text-sm mt-3">
            Capture los datos del paciente y estudios directamente en el formulario
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClinicalWorkflow;
